import { Box, IconButton, Tooltip } from '@mui/material';
import { Focus } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import useRoi from '~/hooks/structure-set/useRoi';
import useViewportFocus from '~/hooks/useViewportFocus';
import StructureReviewStatus from '../StructureReviewStatus';

export default function StructureActions({
    roi,
    isSelected,
    approvalStatus,
}: {
    roi: RoiReadModel;
    isSelected: boolean;
    approvalStatus?: DICOMApprovalStatus;
}) {
    const intl = useIntl();
    const { setVisible, setRoiSelected } = useRoi(roi);
    const { focusOnStructure, focusOnPoint } = useViewportFocus();

    const handleFocus = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isSelected) {
            setRoiSelected();
        }
        if (!roi.visible) {
            setVisible(true);
        }
        if (roi.isPointRoi && roi.pointCoordinatesMm) {
            focusOnPoint(roi.pointCoordinatesMm);
        } else {
            focusOnStructure(roi.roiNumber);
        }
    };

    const renderApprovalStatus =
        approvalStatus === DICOMApprovalStatus.APPROVED ||
        approvalStatus === DICOMApprovalStatus.REJECTED;

    return (
        <Box display="flex" alignItems="center" mr={-1}>
            <Box
                data-testid={`roi-actions-${roi.name}`}
                className="checkbox-container"
                sx={{
                    visibility: isSelected ? 'visible' : 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Tooltip title={<FormattedMessage id="structure.focus" />}>
                    <IconButton
                        color="primary"
                        size="small"
                        onClick={handleFocus}
                        data-testid={`structure-focus-button-${roi.name}`}
                        aria-label={intl.formatMessage({
                            id: 'structure.focus',
                        })}
                    >
                        <Focus size={12} />
                    </IconButton>
                </Tooltip>
            </Box>
            {renderApprovalStatus && (
                <Box display="grid" alignItems="center" mx={0.5}>
                    <StructureReviewStatus status={approvalStatus} />
                </Box>
            )}
        </Box>
    );
}
